// Sentry — instrumentation client (Qorum frontend).
// DSN public, safe à embarquer côté client.

import { CONSENT_EVENT, getConsent } from "./cookie-consent";

const DSN =
  "https://85794d68ef1f9e70ab217081474f236d@o4511468087148544.ingest.de.sentry.io/4511468105367632";

let initialized = false;
let sentryModulePromise: Promise<typeof import("@sentry/react")> | undefined;
let consentListenerAttached = false;

function loadSentry() {
  sentryModulePromise ??= import("@sentry/react");
  return sentryModulePromise;
}

function hasMonitoringConsent() {
  return getConsent().categories.monitoring === true;
}

function attachConsentListener() {
  if (consentListenerAttached || typeof window === "undefined") return;
  consentListenerAttached = true;
  window.addEventListener(CONSENT_EVENT, () => {
    if (!initialized && hasMonitoringConsent()) {
      void initSentry();
    }
  });
}

export async function initSentry() {
  attachConsentListener();
  if (initialized) return;
  if (typeof window === "undefined") return;
  // Gate non-essentiel (CNIL) — sans consentement, on n'init pas Sentry.
  if (!hasMonitoringConsent()) return;
  initialized = true;
  const Sentry = await loadSentry();

  const release =
    (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_COMMIT_HASH) ||
    undefined;

  Sentry.init({
    dsn: DSN,
    release,
    enabled:
      typeof window !== "undefined" &&
      (window.location.hostname === "hojin.ai" ||
        window.location.hostname.endsWith(".hojin.ai")),
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
    beforeSend(event) {
      if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        return null;
      }
      return event;
    },
  });
}

export async function captureSentryException(error: unknown) {
  if (typeof window === "undefined") return;
  if (!hasMonitoringConsent()) return;
  await initSentry();
  if (!initialized) return;
  const Sentry = await loadSentry();
  Sentry.captureException(error);
}

export async function setSentryUser(user: { id: string; email?: string } | null) {
  if (typeof window === "undefined") return;
  if (!hasMonitoringConsent()) return;
  await initSentry();
  if (!initialized) return;
  const Sentry = await loadSentry();
  Sentry.setUser(user);
}
